/**
 * Canonical display names for common technical skills.
 * Keys are lowercase; values are the preferred display casing.
 */
const SKILL_CANONICAL = {
  python: "Python",
  react: "React",
  fastapi: "FastAPI",
  javascript: "JavaScript",
  typescript: "TypeScript",
  html: "HTML",
  css: "CSS",
  "machine learning": "Machine Learning",
  "deep learning": "Deep Learning",
  llm: "LLM",
  pandas: "Pandas",
  numpy: "NumPy",
  "scikit-learn": "Scikit-learn",
  git: "Git",
  github: "GitHub",
  docker: "Docker",
  mysql: "MySQL",
  mongodb: "MongoDB",
  postgresql: "PostgreSQL",
  sqlalchemy: "SQLAlchemy",
  "rest api": "REST API",
  jwt: "JWT",
  api: "API",
  node: "Node.js",
  "node.js": "Node.js",
  vue: "Vue.js",
  angular: "Angular",
  java: "Java",
  spring: "Spring",
  kotlin: "Kotlin",
  redis: "Redis",
  kubernetes: "Kubernetes",
  aws: "AWS",
  azure: "Azure",
  tensorflow: "TensorFlow",
  pytorch: "PyTorch",
};

const ACRONYMS = new Set(["html", "css", "api", "jwt", "llm", "aws", "sql"]);

/**
 * Parse skills from API response (JSON string, array, or plain string).
 */
export function parseSkillsInput(raw) {
  if (raw == null || raw === "") return [];

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => parseSkillsInput(item));
  }

  if (typeof raw === "object") {
    return [JSON.stringify(raw)];
  }

  const trimmed = String(raw).trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      return parseSkillsInput(parsed);
    } catch {
      // Fall through to string handling
    }
  }

  if (trimmed.includes(",")) {
    return trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  }

  return [trimmed];
}

/**
 * Score how "proper" a skill label looks for display preference.
 */
function casingScore(label) {
  if (!label) return 0;
  if (SKILL_CANONICAL[label.toLowerCase()]) return 100;
  if (/^[A-Z][a-z]/.test(label)) return 80;
  if (/[A-Z]/.test(label) && !/^[A-Z]+$/.test(label)) return 70;
  if (/^[a-z]/.test(label)) return 30;
  return 50;
}

/**
 * Normalize a single skill label to its canonical display form.
 */
export function formatSkillLabel(skill) {
  const trimmed = String(skill).trim();
  if (!trimmed) return "";

  const key = trimmed.toLowerCase();
  if (SKILL_CANONICAL[key]) return SKILL_CANONICAL[key];

  if (ACRONYMS.has(key)) return key.toUpperCase();

  if (/^[a-z]/.test(trimmed)) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  return trimmed;
}

/**
 * Deduplicate skills case-insensitively while preserving logical order.
 * Prefers canonical casing (React over react, Git over git).
 */
export function normalizeSkillList(raw) {
  const items = parseSkillsInput(raw);
  const seen = new Map();

  items.forEach((item) => {
    const trimmed = String(item).trim();
    if (!trimmed) return;

    const key = trimmed.toLowerCase();
    const formatted = formatSkillLabel(trimmed);
    const existing = seen.get(key);

    if (!existing) {
      seen.set(key, formatted);
      return;
    }

    const existingScore = casingScore(existing);
    const newScore = casingScore(formatted);
    if (newScore > existingScore) {
      seen.set(key, formatted);
    }
  });

  const result = [];
  const added = new Set();

  items.forEach((item) => {
    const key = String(item).trim().toLowerCase();
    if (!key || added.has(key)) return;
    added.add(key);
    result.push(seen.get(key));
  });

  return result;
}
